import posthog from "posthog-js";
import { ConvexHttpClient } from "convex/browser";
import { api, type Id } from "@formio/convex-api";
import type { MultiEntryFieldDef } from "@/data/questions";
import type { DbFormQuestionRow } from "@/lib/questionMapper";

const convexUrl = import.meta.env.VITE_CONVEX_URL;
export const convexClient = new ConvexHttpClient(convexUrl);

export { api };
export type { Id };

export type OcrContext = {
  firmId: string;
  formType?: string;
  submissionId?: string;
};

// `logOcrTransaction` vivait ici, importé par les deux modules d'OCR... et
// appelé par aucun. Le compteur d'IA de l'app formulaire était donc branché sur
// rien depuis le début. Il est remplacé par une mesure posée dans
// `gemini-client.ts`, à un endroit qu'un appelant ne peut pas oublier.


/**
 * Le lien vers le rejeu PostHog, positionné AVANT le plantage.
 *
 * Trente secondes de recul : ce qui compte n'est pas l'erreur mais la
 * manipulation qui l'a provoquée. Sans ce décalage le rejeu s'ouvre sur un
 * écran déjà cassé, ce qui n'apprend rien.
 *
 * Enveloppé : l'enregistrement peut être désactivé, refusé, ou pas encore prêt.
 * Une erreur ici transformerait une erreur en deux.
 */
function replayUrl(): string | undefined {
  try {
    return posthog.get_session_replay_url({
      withTimestamp: true,
      timestampLookBack: 30,
    });
  } catch {
    return undefined;
  }
}

export const logClientError = (
  context: string,
  error: any,
  meta?: { submissionId?: string; clientId?: string; [key: string]: any },
): void => {
  const { submissionId, clientId, ...details } = meta ?? {};
  convexClient
    .mutation(api.errorLogs.logError, {
      source: "formioform",
      context,
      message: error?.message ?? String(error),
      details: { stack: error?.stack ?? null, ...details },
      replayUrl: replayUrl(),
      pageUrl: typeof window !== "undefined" ? window.location.href : undefined,
      submissionId: submissionId
        ? (submissionId as Id<"submissions">)
        : undefined,
      clientId: clientId ? (clientId as Id<"clients">) : undefined,
    })
    .catch((err) => console.warn("[logClientError] failed to persist:", err));
};

export const buildTranslatedAnswers = async (
  newAnswers: Record<string, unknown>,
  currentTranslated: Record<string, unknown> | null | undefined,
  preferredLang: string,
  questionTypeFor?: (key: string) => string,
  questionLabelFor?: (key: string) => string | undefined,
  multiEntryFieldsFor?: (key: string) => MultiEntryFieldDef[] | undefined,
): Promise<Record<string, unknown> | null> => {
  if (preferredLang === "fr") return null;
  try {
    const { translateAnswer } = await import("./answer-translator");
    const merged: Record<string, unknown> = { ...(currentTranslated || {}) };
    await Promise.all(
      Object.entries(newAnswers).map(async ([key, value]) => {
        try {
          const qType = questionTypeFor?.(key) ?? "text";
          merged[key] = await translateAnswer(value, qType, preferredLang, {
            questionLabel: questionLabelFor?.(key),
            multiEntryFields: multiEntryFieldsFor?.(key),
          });
        } catch {
          merged[key] = value;
        }
      }),
    );
    return merged;
  } catch {
    return null;
  }
};

/** Stable per-file key shared with the wizard so verification verdicts map to files. */
export function fileSignature(f: File): string {
  return `${f.name}|${f.size}|${f.lastModified}`;
}

// An upload is three network round trips (get a URL, POST the bytes, register
// the file). On a phone, any of them can drop mid-way — Safari reports it as
// "Load failed" — and the client used to get a red "Erreur de sauvegarde" for
// five seconds while the wizard moved on as if the document were saved. Each
// step now retries on network failures and 5xx, with a short back-off. A 4xx
// (413 too large, 400) is final and reported as is.
const UPLOAD_ATTEMPTS = 3;
const isTransient = (err: unknown) =>
  err instanceof TypeError || // fetch network failure ("Load failed", "Failed to fetch")
  (err instanceof UploadHttpError && err.status >= 500);

export class UploadHttpError extends Error {
  status: number;
  constructor(status: number) {
    super(`Upload failed: ${status}`);
    this.status = status;
  }
}

async function withRetry<T>(step: () => Promise<T>): Promise<T> {
  let last: unknown;
  for (let attempt = 0; attempt < UPLOAD_ATTEMPTS; attempt++) {
    try {
      return await step();
    } catch (err) {
      last = err;
      if (!isTransient(err)) throw err;
      await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
    }
  }
  throw last;
}

export async function uploadDocument(
  submissionId: string,
  fieldId: string,
  file: File,
  verification?: { status: string; feedback?: string },
): Promise<{ storageId: string | null; error: any }> {
  try {
    const url = await withRetry(() =>
      convexClient.mutation(api.submissionDocuments.generateUploadUrl, {}),
    );
    const storageId = await withRetry(async () => {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!res.ok) throw new UploadHttpError(res.status);
      return (await res.json()).storageId as string;
    });
    await withRetry(() =>
      convexClient.mutation(api.submissionDocuments.registerDocument, {
        submissionId: submissionId as Id<"submissions">,
        name: fieldId,
        storageId: storageId as Id<"_storage">,
        fileType: file.type,
        ...(verification
          ? {
              verificationStatus: verification.status,
              verificationFeedback: verification.feedback,
            }
          : {}),
      }),
    );
    return { storageId, error: null };
  } catch (err) {
    return { storageId: null, error: err };
  }
}

export function toDbFormQuestionRows(convexRows: any[]): DbFormQuestionRow[] {
  return convexRows.map((row) => ({
    order_index: row.orderIndex,
    section: row.section ?? null,
    section_translations: row.sectionTranslations ?? null,
    depends_on: row.dependsOn ?? null,
    label_override: row.labelOverride ?? null,
    named_label: row.namedLabel ?? null,
    required_override: row.requiredOverride ?? null,
    questions: row.question
      ? {
          id: row.question.externalId,
          label: row.question.label,
          type: row.question.type,
          indication: row.question.indication ?? null,
          help: row.question.help ?? null,
          example: row.question.example ?? null,
          placeholder: row.question.placeholder ?? null,
          is_required: row.question.isRequired ?? true,
          requires_confirmation: row.question.requiresConfirmation ?? false,
          confirmation_text: row.question.confirmationText ?? null,
          success_message: row.question.successMessage ?? null,
          why_important_reason: row.question.whyImportantReason ?? null,
          why_important_consequence: row.question.whyImportantConsequence ?? null,
          options: row.question.options ?? null,
          document_config: row.question.documentConfig ?? null,
          validation_rules: row.question.validationRules ?? null,
          multi_entry_fields: row.question.multiEntryFields ?? null,
          multi_entry_add_label: row.question.multiEntryAddLabel ?? null,
          details_box_label: row.question.detailsBoxLabel ?? null,
          translations: row.question.translations ?? null,
        }
      : null,
  }));
}
